import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
// import { Button } from '@progress/kendo-react-buttons'

import {
  useLazyGetSystemQuery,
  useUpdatePasswordSettingsMutation,
} from 'app/services/system.service'
// import colors from 'utils/colors'
import { systemActions } from 'app/slices/system.slice'
import { BUTTONS, TITLES, FORM, ERRORS } from 'locales/locales'

import Loading from 'components/spinner/Loading'
import CustomText from 'components/text/CustomText'
import CustomButton from 'components/button/CustomButton'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, SettingsPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import KendoSwitch from 'components/kendo/switch/Switch'
import { NumericTextBox } from '@progress/kendo-react-inputs'
import { FixMeLater } from 'types'

const AdminPasswordSettings: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [getPasswordSettings] = useLazyGetSystemQuery()
  const [updatePasswordSettings] = useUpdatePasswordSettingsMutation()
  const systemSettings = useAppSelector((state) => state.system.data)
  
  const [passwordSettings, setPasswordSettings] = useState(systemSettings?.passwordSettings)
  
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasPasswordUpdatePermission = isFlagSetIn(
    [SettingsPermissions.UPDATE_PASSWORD_SETTINGS],
    permissions.settings,
  )

  useEffect(() => {
    fetchPasswordSettings()
  }, [])

  const fetchPasswordSettings = useCallback(async () => {
    try {
      Loading.ref.show()
      const data = await getPasswordSettings({})
      if (data?.data?.passwordSettings) {
        setPasswordSettings(data.data.passwordSettings)
      }
    } catch (error) {
      console.log(error)
    } finally {
      Loading.ref.hide()
    }
  }, [getPasswordSettings])

  const onSubmitHandler = async () => {
    if (
      Number(passwordSettings.uniquecharacters) < 0 ||
      Number(passwordSettings.mincharactes) < 0
    ) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.INVALID_PASSWORD_SETTINGS),
      })
      return
    }
    Loading.ref.show()
    try {
      const response: FixMeLater = await updatePasswordSettings({ ...passwordSettings })
      if (response?.data?.success) {
        dispatch(systemActions.updatePasswordSettings(passwordSettings))
      }
    } finally {
      Loading.ref.hide()
    }
  }

  const onChangeInput = (key: string, value: string | boolean | number) => {
    value = ['uniquecharacters', 'mincharactes'].includes(key) ? Number(value) : value
    setPasswordSettings({ ...passwordSettings, [key]: value })
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.PASSWORD_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <Layout>
          <Side>
            <LeftSide>
              <SwitchWrapper>
                <CustomText typography="header2">{t(FORM.LOWER_CASE_REQUIRED)}</CustomText>
                <div>
                  <KendoSwitch
                    checked={passwordSettings.lowercase}
                    onChange={(checked: boolean) => onChangeInput('lowercase', checked)}
                  />
                </div>
              </SwitchWrapper>
              <SwitchWrapper>
                <CustomText typography="header2">{t(FORM.UPPER_CASE_REQUIRED)}</CustomText>
                <div>
                  <KendoSwitch
                    checked={passwordSettings.uppercase}
                    onChange={(checked: boolean) => onChangeInput('uppercase', checked)}
                  />
                </div>
              </SwitchWrapper>
              <SwitchWrapper>
                <CustomText typography="header2">{t(FORM.LETTER_CHAR_REQUIRED)}</CustomText>
                <div>
                  <KendoSwitch
                    checked={passwordSettings.letter}
                    onChange={(checked: boolean) => onChangeInput('letter', checked)}
                  />
                </div>
              </SwitchWrapper>
              <SwitchWrapper>
                <CustomText typography="header2">{t(FORM.NUMBER_REQUIRED)}</CustomText>
                <div>
                  <KendoSwitch
                    checked={passwordSettings.number}
                    onChange={(checked: boolean) => onChangeInput('number', checked)}
                  />
                </div>
              </SwitchWrapper>
              <NumberInputWrapper>
                <CustomText typography="header2">{t(FORM.UNIQUE_CHARACTER_REQUIRED)}</CustomText>
                <NumericTextBox
                  width={200}
                  size="large"
                  defaultValue={Number(passwordSettings.uniquecharacters)}
                  format="n0"
                  min={0}
                  max={10}
                  onChange={(e: FixMeLater) => onChangeInput('uniquecharacters', e.value)}
                />
              </NumberInputWrapper>
              <NumberInputWrapper>
                <CustomText typography="header2">{t(FORM.MINIMUM_CHARACTER_REQUIRED)}</CustomText>
                <NumericTextBox
                  size="large"
                  width={200}
                  defaultValue={Number(passwordSettings.mincharactes)}
                  format="n0"
                  min={0}
                  max={20}
                  onChange={(e: FixMeLater) => onChangeInput('mincharactes', e.value)}
                />
              </NumberInputWrapper>
              <VimeButtonWrapper>
                {hasPasswordUpdatePermission ? (
                  <CustomStyledButton
                    text={t(BUTTONS.SAVE)}
                    textColor="white"
                    onClick={onSubmitHandler}
                  />
                ) : null}
              </VimeButtonWrapper>
            </LeftSide>
          </Side>
        </Layout>
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminPasswordSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const CustomStyledButton = styled(CustomButton)`
  background-color: #1f4ab7;
`
const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const SwitchWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px;
  align-items: center;
  margin-bottom: 16px;
  column-gap: 16px;
`

const NumberInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  > div {
    width: 200px;
  }
`

const Layout = styled.div`
  display: flex;
  flex-direction: column;
`

const Side = styled.div`
  max-width: 500px;
  display: flex;
  flex-direction: column;
`

const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
`

const VimeButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`
